import {
  ReactNode,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, Input, useCombobox } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { useDidUpdate, useUncontrolled } from '@mantine/hooks';
import clsx from 'clsx';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import utc from 'dayjs/plugin/utc';

import { useFormattingLocale } from '../../utils/getFormattingLocales';
import { Button } from '../buttons/Button';
import { FormLabel } from '../formlabel/FormLabel';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import { ensureDayjsLocales } from './helper/ensureDayjsLocales';
import { isSameDay } from './helper/isSameDay';
import {
  dateToMantineDateString,
  mantineDateStringToDate,
} from './helper/mantineDateStrings';
import { useIsDayExcluded } from './helper/useIsDayExcluded';
import { DateInput } from './DateInput';

import calendarStyles from './calendar.module.scss';
import styles from './date-picker.module.scss';

ensureDayjsLocales();

dayjs.extend(quarterOfYear);
dayjs.extend(utc);

function getStartOfQuarter(date = new Date()) {
  return dayjs.utc(date).startOf('quarter').toDate();
}

function getStartOfYear(date = new Date()) {
  return dayjs.utc(date).startOf('year').toDate();
}

function getYearEarlier(date = new Date()) {
  return dayjs.utc(date).subtract(1, 'year').toDate();
}

export type DateRange = {
  start?: Date;
  end?: Date;
};

export type DateRangePickerProps = {
  label?: ReactNode | ReactNode[];
  /** default value (uncontrolled) **/
  defaultValue?: DateRange;
  /** value (controlled) **/
  value?: DateRange;
  placeholderFrom?: string;
  placeholderTo?: string;
  disabled?: boolean;
  loading?: boolean;
  onCommit?: (range: DateRange) => void;
  onChange?: (range: DateRange) => void;
  availableDates?: Date[];
  dateTimeFormatOptions?: Intl.DateTimeFormatOptions;
  withinPortal?: boolean;
  /** left-side icon. Defaults to calendar-icon (event) */
  icon?: string;
};

const getOppositeRange = {
  start: 'end',
  end: 'start',
} as const;

/**
 * Test if the given dates are of the same day. Will return false if one date is null
 */
export function isSameDayRange(
  range: DateRange,
  start: Date | undefined,
  end: Date | undefined
) {
  return isSameDay(range.start, start) && isSameDay(range.end, end);
}

export function DateRangePicker({
  value,
  defaultValue = {},
  label,
  placeholderFrom = 'From',
  placeholderTo = 'To',
  onCommit,
  onChange,
  disabled,
  loading,
  dateTimeFormatOptions,
  availableDates,
  withinPortal = true,
  icon = 'event',
}: DateRangePickerProps) {
  const id = useId();
  const isDisabled = (disabled || loading) === true;
  const { t } = useTranslation();
  const locale = useFormattingLocale();
  const [currentDate, setCurrentDate] = useState(new Date());
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);
  const [range, setRange] = useUncontrolled({
    value,
    defaultValue,
    onChange: (value) => {
      onChange?.(value);
    },
  });
  const [lastValue, setLastValue] = useState<DateRange>(range);
  const [focusedDate, setFocusedDate] = useState<'start' | 'end' | undefined>();
  const oppositeRange = focusedDate ? getOppositeRange[focusedDate] : undefined;
  const isDayExcluded = useIsDayExcluded(availableDates);
  const combobox = useCombobox({
    onDropdownOpen: () => setLastValue(range),
    onDropdownClose: () => {
      setFocusedDate(undefined);
    },
  });

  const setPeriod = useCallback(
    (range: DateRange) => {
      setRange(range);
      setFocusedDate('end');
      startRef.current?.blur();
      endRef.current?.blur();
    },
    [setRange, endRef, startRef]
  );

  const period = useMemo(
    () => ({
      /** quarter to date */
      qtd: () => setPeriod({ start: getStartOfQuarter(), end: new Date() }),
      isQTD: (range: DateRange) =>
        isSameDayRange(range, getStartOfQuarter(), new Date()),
      /** year to date */
      ytd: () => setPeriod({ start: getStartOfYear(), end: new Date() }),
      isYtd: (range: DateRange) =>
        isSameDayRange(range, getStartOfYear(), new Date()),
      /** last twelve months */
      ltm: () => setPeriod({ start: getYearEarlier(), end: new Date() }),
      isLtm: (range: DateRange) =>
        isSameDayRange(range, getYearEarlier(), new Date()),
      /** all dates until today */
      sinceInception: () => setPeriod({ start: undefined, end: new Date() }),
      isSinceInception: (range: DateRange) =>
        isSameDayRange(range, undefined, new Date()),
    }),
    [setPeriod]
  );

  // use effect to automatically proceed after combobox has clossed
  // Cannot be done in Calendar component directly, as this would lead to race conditions:
  // The updated range would not be visible yet when closeDropdown is called, and thus the
  // updated end date would not be visible in onDropdownClose.
  useDidUpdate(() => {
    if (combobox.dropdownOpened) {
      return;
    }

    if (
      !isSameDay(lastValue.start, range.start) ||
      !isSameDay(lastValue.end, range.end)
    ) {
      onCommit?.(range);
      setLastValue(range);
    }
  }, [combobox.dropdownOpened]);

  function isInRange(date: Date) {
    if (range.end == null || range.start == null) {
      return false;
    }
    const afterStart = dayjs(range.start).isBefore(date);
    const beforeEnd = dayjs(range.end).isAfter(date);
    return afterStart && beforeEnd;
  }

  return (
    <Combobox
      unstyled
      data-testid="date-range-picker"
      disabled={disabled || loading}
      store={combobox}
      withinPortal={withinPortal}
      classNames={{
        dropdown: styles['dropdown'],
        options: styles['options'],
        header: styles['dropdown-header'],
      }}
    >
      <Combobox.EventsTarget>
        <Input.Wrapper
          id={id}
          classNames={{
            root: styles['root'],
            label: clsx(styles['label'], {
              [styles['label--disabled']]: disabled,
            }),
          }}
          onBlur={() =>
            setTimeout(() => {
              // we wait for a switch in input fields here. In case we still
              // have a focussed input, keep the dropdown active and don't commit
              if (
                document.activeElement !== endRef.current &&
                document.activeElement !== startRef.current
              ) {
                combobox.closeDropdown();
              }
            })
          }
        >
          <FormLabel
            id={id}
            disabled={disabled}
            loading={loading}
            label={label}
          />
          <Combobox.DropdownTarget>
            <div className={styles['dates-wrapper']}>
              <DateInput
                id={id}
                clearable={true}
                data-testid="date-range-start"
                ref={startRef}
                disabled={isDisabled}
                icon={icon}
                loading={loading}
                focused={focusedDate === 'start'}
                value={range.start}
                placeholder={placeholderFrom}
                dateTimeFormatOptions={dateTimeFormatOptions}
                onChange={(date) => {
                  setCurrentDate(date);

                  const selectable =
                    !range.end || !dayjs(date).isAfter(dayjs(range.end));

                  if (!isDayExcluded(date) && selectable) {
                    setRange({ ...range, start: date });
                  }
                }}
                onClear={() => {
                  setRange({ ...range, start: undefined });
                  onCommit?.({ ...range, start: undefined });
                  setLastValue({ ...range, start: undefined });
                }}
                onFocus={() => {
                  setFocusedDate('start');
                  if (range.start) {
                    setCurrentDate(range.start);
                  }
                  combobox.openDropdown();
                }}
              />
              <div
                className={styles['input-separator']}
                {...(isDisabled ? { 'data-disabled': '' } : {})}
              >
                -
              </div>
              <DateInput
                data-testid="date-range-end"
                ref={endRef}
                clearable={true}
                disabled={isDisabled}
                loading={loading}
                icon={icon}
                focused={focusedDate === 'end'}
                value={range.end}
                placeholder={placeholderTo}
                dateTimeFormatOptions={dateTimeFormatOptions}
                onChange={(date) => {
                  setCurrentDate(date);

                  const selectable =
                    !range.start || !dayjs(date).isBefore(dayjs(range.start));

                  if (!isDayExcluded(date) && selectable) {
                    setRange({ ...range, end: date });
                  }
                }}
                onClear={() => {
                  setRange({ ...range, end: undefined });
                  onCommit?.({ ...range, end: undefined });
                  setLastValue({ ...range, end: undefined });
                }}
                onFocus={() => {
                  setFocusedDate('end');
                  if (range.end) {
                    setCurrentDate(range.end);
                  }
                  combobox.openDropdown();
                }}
              />
            </div>
          </Combobox.DropdownTarget>
        </Input.Wrapper>
      </Combobox.EventsTarget>

      <Combobox.Dropdown>
        <GoogleFontIcon
          name="arrow_drop_up"
          solid
          data-position={focusedDate}
          className={styles['dropdown-arrow']}
        />
        {/* combobox options prevent closing dropdown when interacting with contents */}
        <Combobox.Options>
          <Calendar
            locale={locale}
            data-testid="date-range-calendar"
            date={dateToMantineDateString(currentDate)}
            data-range={focusedDate}
            onDateChange={(date) =>
              setCurrentDate(mantineDateStringToDate(date) ?? currentDate)
            }
            excludeDate={isDayExcluded}
            classNames={{
              calendarHeaderControl: calendarStyles['headerControl'],
              calendarHeaderLevel: calendarStyles['headerLevel'],
              day: calendarStyles['day'],
              monthsListCell: calendarStyles['month'],
              yearsListCell: calendarStyles['year'],
            }}
            getDayProps={(dateString) => {
              const date = mantineDateStringToDate(dateString);
              if (!date) {
                return {};
              }

              const inRange = isInRange(date);

              let unselectable = false;
              if (focusedDate === 'start' && range.end) {
                unselectable = dayjs(date).isAfter(dayjs(range.end));
              } else if (focusedDate === 'end' && range.start) {
                unselectable = dayjs(date).isBefore(dayjs(range.start));
              }

              const isCurrentDaySelection =
                focusedDate && range[focusedDate]
                  ? dayjs(date).isSame(range[focusedDate], 'date')
                  : false;

              const isOppositeDaySelection =
                oppositeRange && range[oppositeRange]
                  ? dayjs(date).isSame(range[oppositeRange], 'date')
                  : false;

              return {
                // if the day is in range of selection (excludes start and end day) [data-in-range=true]
                inRange,
                // if the day is outside the valid range for the current selection type [data-unselectable=true]
                'data-unselectable': unselectable ? true : undefined,
                // if the day is the start or end of date-range [data-selector=true]
                'data-selector':
                  isCurrentDaySelection || isOppositeDaySelection || undefined,
                // if this is the current selected entry [data-selected=true]
                selected: isCurrentDaySelection,
                onClick: () => {
                  if (focusedDate == null || unselectable) {
                    return;
                  }

                  if (date.toString() === range[focusedDate]?.toString()) {
                    setRange({ ...range, [focusedDate]: undefined });
                  } else {
                    setRange({ ...range, [focusedDate]: date });
                  }

                  if (focusedDate === 'start' && range.end == null) {
                    endRef.current?.focus();
                  } else {
                    combobox.closeDropdown();
                  }
                },
              };
            }}
          />
          <div className={styles['period-selection']}>
            <Button
              variant={period.isQTD(range) ? 'primary' : 'secondary'}
              title={t('app.datepicker.qtd')}
              onClick={period.qtd}
            >
              {t('app.datepicker.qtdShort')}
            </Button>
            <Button
              variant={period.isYtd(range) ? 'primary' : 'secondary'}
              title={t('app.datepicker.ytd')}
              onClick={period.ytd}
            >
              {t('app.datepicker.ytdShort')}
            </Button>
            <Button
              variant={period.isLtm(range) ? 'primary' : 'secondary'}
              title={t('app.datepicker.ltm')}
              onClick={period.ltm}
            >
              {t('app.datepicker.ltmShort')}
            </Button>
            <Button
              variant={period.isSinceInception(range) ? 'primary' : 'secondary'}
              title={t('app.datepicker.sinceInception')}
              onClick={period.sinceInception}
            >
              {t('app.datepicker.sinceInceptionShort')}
            </Button>
          </div>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
